import * as z from 'zod';

export const CreateAvailabilityExceptionRequest = z.object({
  availabilityException: z
    .object({
      _id: z
        .string()
        .describe('Availability exception ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
        )
        .optional()
        .nullable(),
      _createdDate: z
        .date()
        .describe(
          'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      startTime: z
        .date()
        .describe(
          'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        ),
      endTime: z
        .date()
        .describe(
          'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        ),
      available: z
        .boolean()
        .describe(
          'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
        ),
      name: z
        .string()
        .describe('Exception name.')
        .max(500)
        .optional()
        .nullable(),
      affectedFulfillmentMethods: z
        .intersection(
          z.object({
            affectedMethods: z.enum([
              'ALL_FULFILLMENT_METHODS',
              'SPECIFIC_FULFILLMENT_METHODS',
              'ALL_PICKUP_FULFILLMENT_METHODS',
              'ALL_DELIVERY_FULFILLMENT_METHODS',
            ]),
          }),
          z.xor([
            z.object({
              specificFulfillmentMethodsOptions: z.never().optional(),
            }),
            z.object({
              specificFulfillmentMethodsOptions: z
                .object({
                  fulfillmentMethodIds: z.array(z.string()).max(500).optional(),
                })
                .describe('Settings for specific fulfillment methods.'),
            }),
          ])
        )
        .describe('Fulfillment methods for which this exception applies.'),
      operationId: z
        .string()
        .describe(
          'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        ),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
        )
        .optional(),
    })
    .describe('Availability exception details.'),
});
export const CreateAvailabilityExceptionResponse = z.object({
  _id: z
    .string()
    .describe('Availability exception ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  startTime: z
    .date()
    .describe(
      'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  endTime: z
    .date()
    .describe(
      'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  available: z
    .boolean()
    .describe(
      'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
    )
    .optional()
    .nullable(),
  name: z.string().describe('Exception name.').max(500).optional().nullable(),
  affectedFulfillmentMethods: z
    .intersection(
      z.object({
        affectedMethods: z
          .enum([
            'ALL_FULFILLMENT_METHODS',
            'SPECIFIC_FULFILLMENT_METHODS',
            'ALL_PICKUP_FULFILLMENT_METHODS',
            'ALL_DELIVERY_FULFILLMENT_METHODS',
          ])
          .describe(
            'The type of the fulfillment methods this exception applies to.'
          )
          .optional(),
      }),
      z.xor([
        z.object({ specificFulfillmentMethodsOptions: z.never().optional() }),
        z.object({
          specificFulfillmentMethodsOptions: z
            .object({
              fulfillmentMethodIds: z.array(z.string()).max(500).optional(),
            })
            .describe('Settings for specific fulfillment methods.'),
        }),
      ])
    )
    .describe('Fulfillment methods for which this exception applies.')
    .optional(),
  operationId: z
    .string()
    .describe(
      'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
    )
    .optional(),
});
export const GetAvailabilityExceptionRequest = z.object({
  availabilityExceptionId: z
    .string()
    .describe('ID of the availability exception to retrieve.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const GetAvailabilityExceptionResponse = z.object({
  _id: z
    .string()
    .describe('Availability exception ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  startTime: z
    .date()
    .describe(
      'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  endTime: z
    .date()
    .describe(
      'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  available: z
    .boolean()
    .describe(
      'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
    )
    .optional()
    .nullable(),
  name: z.string().describe('Exception name.').max(500).optional().nullable(),
  affectedFulfillmentMethods: z
    .intersection(
      z.object({
        affectedMethods: z
          .enum([
            'ALL_FULFILLMENT_METHODS',
            'SPECIFIC_FULFILLMENT_METHODS',
            'ALL_PICKUP_FULFILLMENT_METHODS',
            'ALL_DELIVERY_FULFILLMENT_METHODS',
          ])
          .describe(
            'The type of the fulfillment methods this exception applies to.'
          )
          .optional(),
      }),
      z.xor([
        z.object({ specificFulfillmentMethodsOptions: z.never().optional() }),
        z.object({
          specificFulfillmentMethodsOptions: z
            .object({
              fulfillmentMethodIds: z.array(z.string()).max(500).optional(),
            })
            .describe('Settings for specific fulfillment methods.'),
        }),
      ])
    )
    .describe('Fulfillment methods for which this exception applies.')
    .optional(),
  operationId: z
    .string()
    .describe(
      'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
    )
    .optional(),
});
export const UpdateAvailabilityExceptionRequest = z.object({
  _id: z
    .string()
    .describe('Availability exception ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
  availabilityException: z
    .object({
      _id: z
        .string()
        .describe('Availability exception ID.')
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      revision: z
        .string()
        .regex(/^-?\d+$/, 'Must be a valid Int64 string')
        .describe(
          'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
        ),
      _createdDate: z
        .date()
        .describe(
          'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      _updatedDate: z
        .date()
        .describe(
          'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      startTime: z
        .date()
        .describe(
          'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      endTime: z
        .date()
        .describe(
          'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
        )
        .optional()
        .nullable(),
      available: z
        .boolean()
        .describe(
          'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
        )
        .optional()
        .nullable(),
      name: z
        .string()
        .describe('Exception name.')
        .max(500)
        .optional()
        .nullable(),
      affectedFulfillmentMethods: z
        .intersection(
          z.object({
            affectedMethods: z
              .enum([
                'ALL_FULFILLMENT_METHODS',
                'SPECIFIC_FULFILLMENT_METHODS',
                'ALL_PICKUP_FULFILLMENT_METHODS',
                'ALL_DELIVERY_FULFILLMENT_METHODS',
              ])
              .optional(),
          }),
          z.xor([
            z.object({
              specificFulfillmentMethodsOptions: z.never().optional(),
            }),
            z.object({
              specificFulfillmentMethodsOptions: z
                .object({
                  fulfillmentMethodIds: z.array(z.string()).max(500).optional(),
                })
                .describe('Settings for specific fulfillment methods.'),
            }),
          ])
        )
        .describe('Fulfillment methods for which this exception applies.')
        .optional(),
      operationId: z
        .string()
        .describe(
          'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      extendedFields: z
        .object({
          namespaces: z
            .record(z.string(), z.record(z.string(), z.any()))
            .describe(
              'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
            )
            .optional(),
        })
        .describe('Extended fields.')
        .optional(),
      businessLocationId: z
        .string()
        .describe(
          'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
        )
        .regex(
          /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
          'Must be a valid GUID'
        )
        .optional()
        .nullable(),
      tags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe(
          'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
        )
        .optional(),
    })
    .describe('Availability exception to update.'),
});
export const UpdateAvailabilityExceptionResponse = z.object({
  _id: z
    .string()
    .describe('Availability exception ID.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  revision: z
    .string()
    .regex(/^-?\d+$/, 'Must be a valid Int64 string')
    .describe(
      'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
    )
    .optional()
    .nullable(),
  _createdDate: z
    .date()
    .describe(
      'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  _updatedDate: z
    .date()
    .describe(
      'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  startTime: z
    .date()
    .describe(
      'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  endTime: z
    .date()
    .describe(
      'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
    )
    .optional()
    .nullable(),
  available: z
    .boolean()
    .describe(
      'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
    )
    .optional()
    .nullable(),
  name: z.string().describe('Exception name.').max(500).optional().nullable(),
  affectedFulfillmentMethods: z
    .intersection(
      z.object({
        affectedMethods: z
          .enum([
            'ALL_FULFILLMENT_METHODS',
            'SPECIFIC_FULFILLMENT_METHODS',
            'ALL_PICKUP_FULFILLMENT_METHODS',
            'ALL_DELIVERY_FULFILLMENT_METHODS',
          ])
          .describe(
            'The type of the fulfillment methods this exception applies to.'
          )
          .optional(),
      }),
      z.xor([
        z.object({ specificFulfillmentMethodsOptions: z.never().optional() }),
        z.object({
          specificFulfillmentMethodsOptions: z
            .object({
              fulfillmentMethodIds: z.array(z.string()).max(500).optional(),
            })
            .describe('Settings for specific fulfillment methods.'),
        }),
      ])
    )
    .describe('Fulfillment methods for which this exception applies.')
    .optional(),
  operationId: z
    .string()
    .describe(
      'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  extendedFields: z
    .object({
      namespaces: z
        .record(z.string(), z.record(z.string(), z.any()))
        .describe(
          'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
        )
        .optional(),
    })
    .describe('Extended fields.')
    .optional(),
  businessLocationId: z
    .string()
    .describe(
      'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional()
    .nullable(),
  tags: z
    .object({
      privateTags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
        )
        .optional(),
      tags: z
        .object({ tagIds: z.array(z.string()).max(100).optional() })
        .describe(
          'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
        )
        .optional(),
    })
    .describe(
      'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
    )
    .optional(),
});
export const DeleteAvailabilityExceptionRequest = z.object({
  availabilityExceptionId: z
    .string()
    .describe('ID of the availability exception to delete.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const DeleteAvailabilityExceptionResponse = z.object({});
export const QueryAvailabilityExceptionsRequest = z.object({
  query: z
    .object({
      filter: z
        .object({
          _id: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _createdDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          _updatedDate: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          startTime: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          endTime: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          available: z
            .object({
              $eq: z.boolean(),
              $exists: z.boolean(),
              $gt: z.boolean(),
              $gte: z.boolean(),
              $hasAll: z.array(z.boolean()),
              $hasSome: z.array(z.boolean()),
              $in: z.array(z.boolean()),
              $lt: z.boolean(),
              $lte: z.boolean(),
              $ne: z.boolean(),
              $nin: z.array(z.boolean()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          name: z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'affectedFulfillmentMethods.affectedMethods': z
            .object({
              $eq: z.string(),
              $exists: z.boolean(),
              $gt: z.string(),
              $gte: z.string(),
              $hasAll: z.array(z.string()),
              $hasSome: z.array(z.string()),
              $in: z.array(z.string()),
              $lt: z.string(),
              $lte: z.string(),
              $ne: z.string(),
              $nin: z.array(z.string()),
              $startsWith: z.string(),
            })
            .partial()
            .strict()
            .optional(),
          'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds':
            z
              .object({
                $eq: z.any(),
                $exists: z.boolean(),
                $gt: z.any(),
                $gte: z.any(),
                $hasAll: z.array(z.any()),
                $hasSome: z.array(z.any()),
                $in: z.array(z.any()),
                $lt: z.any(),
                $lte: z.any(),
                $ne: z.any(),
                $nin: z.array(z.any()),
                $startsWith: z.string(),
              })
              .partial()
              .strict()
              .optional(),
          $and: z.array(z.any()).optional(),
          $or: z.array(z.any()).optional(),
          $not: z.any().optional(),
        })
        .strict()
        .optional(),
      sort: z
        .array(
          z.object({
            fieldName: z
              .enum([
                '_id',
                '_createdDate',
                '_updatedDate',
                'startTime',
                'endTime',
                'available',
                'name',
                'affectedFulfillmentMethods.affectedMethods',
                'affectedFulfillmentMethods.specificFulfillmentMethodsOptions.fulfillmentMethodIds',
              ])
              .optional(),
            order: z.enum(['ASC', 'DESC']).optional(),
          })
        )
        .optional(),
    })
    .catchall(z.any())
    .describe('WQL expression.'),
});
export const QueryAvailabilityExceptionsResponse = z.object({
  availabilityExceptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Availability exception ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        startTime: z
          .date()
          .describe(
            'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        endTime: z
          .date()
          .describe(
            'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        available: z
          .boolean()
          .describe(
            'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
          )
          .optional()
          .nullable(),
        name: z
          .string()
          .describe('Exception name.')
          .max(500)
          .optional()
          .nullable(),
        affectedFulfillmentMethods: z
          .intersection(
            z.object({
              affectedMethods: z
                .enum([
                  'ALL_FULFILLMENT_METHODS',
                  'SPECIFIC_FULFILLMENT_METHODS',
                  'ALL_PICKUP_FULFILLMENT_METHODS',
                  'ALL_DELIVERY_FULFILLMENT_METHODS',
                ])
                .describe(
                  'The type of the fulfillment methods this exception applies to.'
                )
                .optional(),
            }),
            z.xor([
              z.object({
                specificFulfillmentMethodsOptions: z.never().optional(),
              }),
              z.object({
                specificFulfillmentMethodsOptions: z
                  .object({
                    fulfillmentMethodIds: z
                      .array(z.string())
                      .max(500)
                      .optional(),
                  })
                  .describe('Settings for specific fulfillment methods.'),
              }),
            ])
          )
          .describe('Fulfillment methods for which this exception applies.')
          .optional(),
        operationId: z
          .string()
          .describe(
            'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        businessLocationId: z
          .string()
          .describe(
            'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata')
    .optional(),
});
export const BulkCreateAvailabilityExceptionsRequest = z.object({
  availabilityExceptions: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Availability exception ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe(
            'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe(
            'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          )
          .optional()
          .nullable(),
        startTime: z
          .date()
          .describe(
            'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          ),
        endTime: z
          .date()
          .describe(
            'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
          ),
        available: z
          .boolean()
          .describe(
            'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
          ),
        name: z
          .string()
          .describe('Exception name.')
          .max(500)
          .optional()
          .nullable(),
        affectedFulfillmentMethods: z
          .intersection(
            z.object({
              affectedMethods: z.enum([
                'ALL_FULFILLMENT_METHODS',
                'SPECIFIC_FULFILLMENT_METHODS',
                'ALL_PICKUP_FULFILLMENT_METHODS',
                'ALL_DELIVERY_FULFILLMENT_METHODS',
              ]),
            }),
            z.xor([
              z.object({
                specificFulfillmentMethodsOptions: z.never().optional(),
              }),
              z.object({
                specificFulfillmentMethodsOptions: z
                  .object({
                    fulfillmentMethodIds: z
                      .array(z.string())
                      .max(500)
                      .optional(),
                  })
                  .describe('Settings for specific fulfillment methods.'),
              }),
            ])
          )
          .describe('Fulfillment methods for which this exception applies.'),
        operationId: z
          .string()
          .describe(
            'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          ),
        extendedFields: z
          .object({
            namespaces: z
              .record(z.string(), z.record(z.string(), z.any()))
              .describe(
                'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
              )
              .optional(),
          })
          .describe('Extended fields.')
          .optional(),
        businessLocationId: z
          .string()
          .describe(
            'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
          )
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        tags: z
          .object({
            privateTags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
              )
              .optional(),
            tags: z
              .object({ tagIds: z.array(z.string()).max(100).optional() })
              .describe(
                'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
              )
              .optional(),
          })
          .describe(
            'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
          )
          .optional(),
      })
    )
    .min(1)
    .max(20),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe(
          'Whether the full availability exception entity is returned. Defaults to `true`.'
        )
        .optional(),
    })
    .optional(),
});
export const BulkCreateAvailabilityExceptionsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for availability exception creation.')
          .optional(),
        availabilityException: z
          .object({
            _id: z
              .string()
              .describe('Availability exception ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            startTime: z
              .date()
              .describe(
                'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            endTime: z
              .date()
              .describe(
                'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            available: z
              .boolean()
              .describe(
                'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Exception name.')
              .max(500)
              .optional()
              .nullable(),
            affectedFulfillmentMethods: z
              .intersection(
                z.object({
                  affectedMethods: z
                    .enum([
                      'ALL_FULFILLMENT_METHODS',
                      'SPECIFIC_FULFILLMENT_METHODS',
                      'ALL_PICKUP_FULFILLMENT_METHODS',
                      'ALL_DELIVERY_FULFILLMENT_METHODS',
                    ])
                    .describe(
                      'The type of the fulfillment methods this exception applies to.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    specificFulfillmentMethodsOptions: z.never().optional(),
                  }),
                  z.object({
                    specificFulfillmentMethodsOptions: z
                      .object({
                        fulfillmentMethodIds: z
                          .array(z.string())
                          .max(500)
                          .optional(),
                      })
                      .describe('Settings for specific fulfillment methods.'),
                  }),
                ])
              )
              .describe('Fulfillment methods for which this exception applies.')
              .optional(),
            operationId: z
              .string()
              .describe(
                'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
              )
              .optional(),
          })
          .describe('Created availability exception.')
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the API call.')
    .optional(),
});
export const BulkUpdateAvailabilityExceptionsRequest = z.object({
  availabilityExceptions: z
    .array(
      z.object({
        availabilityException: z
          .object({
            _id: z
              .string()
              .describe('Availability exception ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              ),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
              ),
            _createdDate: z
              .date()
              .describe(
                'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            startTime: z
              .date()
              .describe(
                'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            endTime: z
              .date()
              .describe(
                'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            available: z
              .boolean()
              .describe(
                'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Exception name.')
              .max(500)
              .optional()
              .nullable(),
            affectedFulfillmentMethods: z
              .intersection(
                z.object({
                  affectedMethods: z
                    .enum([
                      'ALL_FULFILLMENT_METHODS',
                      'SPECIFIC_FULFILLMENT_METHODS',
                      'ALL_PICKUP_FULFILLMENT_METHODS',
                      'ALL_DELIVERY_FULFILLMENT_METHODS',
                    ])
                    .optional(),
                }),
                z.xor([
                  z.object({
                    specificFulfillmentMethodsOptions: z.never().optional(),
                  }),
                  z.object({
                    specificFulfillmentMethodsOptions: z
                      .object({
                        fulfillmentMethodIds: z
                          .array(z.string())
                          .max(500)
                          .optional(),
                      })
                      .describe('Settings for specific fulfillment methods.'),
                  }),
                ])
              )
              .describe('Fulfillment methods for which this exception applies.')
              .optional(),
            operationId: z
              .string()
              .describe(
                'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
              )
              .optional(),
          })
          .describe('Availability exception entity to update.')
          .optional(),
        mask: z.array(z.string()).optional(),
      })
    )
    .min(1)
    .max(100),
  options: z
    .object({
      returnEntity: z
        .boolean()
        .describe('Whether to receive the entity in the response.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateAvailabilityExceptionsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata of the availability exception update.')
          .optional(),
        item: z
          .object({
            _id: z
              .string()
              .describe('Availability exception ID.')
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            revision: z
              .string()
              .regex(/^-?\d+$/, 'Must be a valid Int64 string')
              .describe(
                'Revision number, which increments by 1 each time the availability exception is updated.\nTo prevent conflicting changes, the current revision must be specified when updating the availability exception.'
              )
              .optional()
              .nullable(),
            _createdDate: z
              .date()
              .describe(
                'Date and time the availability exception was created in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            _updatedDate: z
              .date()
              .describe(
                'Date and time the availability exception was last updated in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            startTime: z
              .date()
              .describe(
                'The start time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            endTime: z
              .date()
              .describe(
                'The end time of the availability exception in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601#combined_date_and_time_representations) format.'
              )
              .optional()
              .nullable(),
            available: z
              .boolean()
              .describe(
                'Whether the exception makes the [`start_time`, `end_time`] range available.\nIf `true`, the exception makes the restaurant available for online ordering during the time range. If `false`, the exception makes the restaurant unavailable for ordering during the time range.\nCurrently, only `false` is supported.'
              )
              .optional()
              .nullable(),
            name: z
              .string()
              .describe('Exception name.')
              .max(500)
              .optional()
              .nullable(),
            affectedFulfillmentMethods: z
              .intersection(
                z.object({
                  affectedMethods: z
                    .enum([
                      'ALL_FULFILLMENT_METHODS',
                      'SPECIFIC_FULFILLMENT_METHODS',
                      'ALL_PICKUP_FULFILLMENT_METHODS',
                      'ALL_DELIVERY_FULFILLMENT_METHODS',
                    ])
                    .describe(
                      'The type of the fulfillment methods this exception applies to.'
                    )
                    .optional(),
                }),
                z.xor([
                  z.object({
                    specificFulfillmentMethodsOptions: z.never().optional(),
                  }),
                  z.object({
                    specificFulfillmentMethodsOptions: z
                      .object({
                        fulfillmentMethodIds: z
                          .array(z.string())
                          .max(500)
                          .optional(),
                      })
                      .describe('Settings for specific fulfillment methods.'),
                  }),
                ])
              )
              .describe('Fulfillment methods for which this exception applies.')
              .optional(),
            operationId: z
              .string()
              .describe(
                'ID of the restaurant operation this exception is associated with. (See the Restaurants Operations API for more information.)'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            extendedFields: z
              .object({
                namespaces: z
                  .record(z.string(), z.record(z.string(), z.any()))
                  .describe(
                    'Extended field data. Each key corresponds to the namespace of the app that created the extended fields.\nThe value of each key is structured according to the schema defined when the extended fields were configured.\n\nYou can only access fields for which you have the appropriate permissions.\n\nLearn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).'
                  )
                  .optional(),
              })
              .describe('Extended fields.')
              .optional(),
            businessLocationId: z
              .string()
              .describe(
                'Business location ID ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) of the operation this availability exception applies to.'
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            tags: z
              .object({
                privateTags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
                  )
                  .optional(),
                tags: z
                  .object({ tagIds: z.array(z.string()).max(100).optional() })
                  .describe(
                    'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
                  )
                  .optional(),
              })
              .describe(
                'Tags ([SDK](https://dev.wix.com/docs/sdk/backend-modules/tags/tags/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/tags/introduction)) used to classify and sort different types of availability exceptions.'
              )
              .optional(),
          })
          .describe(
            'Availability exception (present when return_entity is true).'
          )
          .optional(),
      })
    )
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Bulk Update Availability Exceptions metadata.')
    .optional(),
});
export const BulkUpdateAvailabilityExceptionTagsRequest = z.object({
  availabilityExceptionIds: z.array(z.string()).max(100),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the availability exceptions.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the availability exceptions.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateAvailabilityExceptionTagsResponse = z.object({
  results: z
    .array(
      z.object({
        itemMetadata: z
          .object({
            _id: z
              .string()
              .describe(
                "Item ID. Should always be available, unless it's impossible (for example, when failing to create an item)."
              )
              .regex(
                /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
                'Must be a valid GUID'
              )
              .optional()
              .nullable(),
            originalIndex: z
              .number()
              .int()
              .describe(
                'Index of the item within the request array. Allows for correlation between request and response items.'
              )
              .optional(),
            success: z
              .boolean()
              .describe(
                'Whether the requested action was successful for this item. When `false`, the `error` field is populated.'
              )
              .optional(),
            error: z
              .object({
                code: z.string().describe('Error code.').optional(),
                description: z
                  .string()
                  .describe('Description of the error.')
                  .optional(),
                data: z
                  .record(z.string(), z.any())
                  .describe('Data related to the error.')
                  .optional()
                  .nullable(),
              })
              .describe('Details about the error in case of failure.')
              .optional(),
          })
          .describe('Metadata for the updated availability exception.')
          .optional(),
      })
    )
    .min(1)
    .max(100)
    .optional(),
  bulkActionMetadata: z
    .object({
      totalSuccesses: z
        .number()
        .int()
        .describe('Number of items that were successfully processed.')
        .optional(),
      totalFailures: z
        .number()
        .int()
        .describe("Number of items that couldn't be processed.")
        .optional(),
      undetailedFailures: z
        .number()
        .int()
        .describe(
          'Number of failures without details because detailed failure threshold was exceeded.'
        )
        .optional(),
    })
    .describe('Metadata for the bulk update.')
    .optional(),
});
export const BulkUpdateAvailabilityExceptionTagsByFilterRequest = z.object({
  filter: z
    .record(z.string(), z.any())
    .describe(
      'Filter that determines which availability exceptions to update tags for.'
    ),
  options: z
    .object({
      assignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to assign to the availability exceptions.')
        .optional(),
      unassignTags: z
        .object({
          privateTags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that require an additional permission in order to access them, normally not given to site members or visitors.'
            )
            .optional(),
          tags: z
            .object({ tagIds: z.array(z.string()).max(100).optional() })
            .describe(
              'Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors.'
            )
            .optional(),
        })
        .describe('Tags to unassign from the availability exceptions.')
        .optional(),
    })
    .optional(),
});
export const BulkUpdateAvailabilityExceptionTagsByFilterResponse = z.object({
  jobId: z
    .string()
    .describe(
      "Job ID. Pass this ID to Get Async Job ([SDK](https://dev.wix.com/docs/sdk/backend-modules/async-jobs/get-async-job) | [REST](https://dev.wix.com/docs/rest/business-management/async-job/get-async-job)) to track the job's status."
    )
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    )
    .optional(),
});
